/**
 * `bootId` — the runtime's per-process identity, and the only primitive that
 * lets this SPA tell "the runtime is still up" from "it restarted under me".
 *
 * ## Why this exists
 *
 * A chat turn interrupted runtime-side used to leave the promptbox
 * PERMANENTLY locked: `activeRunId` is cleared only by a wire event
 * (`finalizeRun` / `error` / `aborted`), and when the runtime goes away no
 * event ever arrives. Reported by `go-live` from a prod incident, confirmed in
 * code (`useChatSession.ts` → `isStreaming: activeRunId !== null`).
 *
 * Core's maintenance timer DOES sweep expired runs and emit the aborted frame
 * (`server-maintenance.ts`) — but only while the event loop is running, and the
 * expiry is `clamp(timeout + grace, 2 min, 24 h)`. So the self-heal is real but
 * neither prompt nor guaranteed. `bootId` gives the missing certainty:
 *
 *   changed on reconnect ⇒ the process that owned the run is GONE ⇒ every
 *                          in-flight run is definitively dead ⇒ abort locally.
 *   unchanged            ⇒ concludes NOTHING. The run may still be alive; core's
 *                          expiry timer is the backstop. Never abort on this.
 *
 * Runtime half built by `lexruntime-changes` in `c8a89cfe7` (miniplan
 * `234d789d2`).
 *
 * ## 🚨 Read `/api/lx/version`, NOT `/api/lx/version-skew`
 *
 * This is a trap with no symptom. lexserver's `/version-skew` does not forward
 * the runtime's payload — `probeRunningVersion` (`runtime-proxy.ts:66-96`)
 * REBUILDS the object from exactly four fields (`version`, `commit`,
 * `builtAt`, `openclawVersion`) and discards everything else. Reading `bootId`
 * off `useRuntimeVersion().skew.running` therefore yields `undefined` FOREVER,
 * silently, and the whole mechanism would look implemented while being inert.
 * Verified against that source, not taken on trust. `/api/lx/version` is
 * forwarded unreshaped through the generic per-user proxy.
 *
 * ## Three states, because a failed read is not an answer
 *
 * `absent` and `unreadable` are deliberately DISTINCT from each other and from
 * a value:
 *   - `absent`     — the runtime predates `c8a89cfe7` (no roll yet). Expected,
 *                    and it must stay expected until the fleet rolls.
 *   - `unreadable` — the request failed. This is the dangerous one to collapse:
 *                    treating it as "unchanged" keeps the lock (the bug), and
 *                    treating it as "changed" would ABORT A LIVE RUN and throw
 *                    away the lawyer's turn. Neither guess is acceptable, so it
 *                    is its own state and the caller declines to act.
 *
 * ⚠ A new `bootId` is NOT always an incident. A lextools bundle promote
 * restarts the process (T1-idle restart activation), so it legitimately mints a
 * new one and legitimately kills in-flight runs. Aborting there is CORRECT —
 * the runs really are dead — so this must never be surfaced as an error.
 */

import { getToken } from "@/lib/lexiun-jwt";
import { newTraceparent } from "@/lib/traceparent";

/** Result of one boot-identity read. See the three-states note above. */
export type RuntimeBootRead =
  | { state: "present"; bootId: string; startedAt?: string }
  /** Runtime is reachable but predates `c8a89cfe7` — cannot tell, not a failure. */
  | { state: "absent" }
  /** The read itself failed. Cannot tell, and MUST NOT be read as either branch. */
  | { state: "unreadable"; reason: string };

/**
 * Read the runtime's boot identity from `GET /api/lx/version`.
 *
 * Never throws — every failure is expressed as `unreadable`, because this is
 * called on a reconnect path where an exception would be swallowed anyway and
 * a swallowed exception here means "silently stop protecting the user".
 */
export async function readRuntimeBoot(signal?: AbortSignal): Promise<RuntimeBootRead> {
  try {
    const token = await getToken();
    const res = await fetch("/api/lx/version", {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        traceparent: newTraceparent(),
      },
      // Belt-and-braces: the runtime handler already sets `Cache-Control:
      // no-store` (`version.ts`), but a cached read here would silently report
      // "unchanged" forever and disable the mechanism — the one failure mode
      // that looks exactly like working correctly.
      cache: "no-store",
      signal,
    });
    if (!res.ok) return { state: "unreadable", reason: `HTTP ${res.status}` };
    const ct = res.headers.get("content-type") ?? "";
    if (!ct.includes("application/json")) {
      return { state: "unreadable", reason: `non-JSON (${ct || "no content-type"})` };
    }
    const body = (await res.json()) as { bootId?: unknown; startedAt?: unknown };
    if (typeof body?.bootId !== "string" || body.bootId === "") {
      // Present-but-not-a-string is the same actionable situation as missing:
      // we have no identity to compare. Reported as `absent` (cannot tell)
      // rather than `unreadable`, because the request itself succeeded.
      return { state: "absent" };
    }
    return {
      state: "present",
      bootId: body.bootId,
      startedAt: typeof body.startedAt === "string" ? body.startedAt : undefined,
    };
  } catch (err) {
    return { state: "unreadable", reason: (err as Error)?.message ?? "fetch failed" };
  }
}

/**
 * Did the runtime demonstrably restart between these two reads?
 *
 * ⚠ Answers `true` ONLY when both reads produced a real id and they differ.
 * Every other combination — either side absent, either side unreadable — is
 * `false`, meaning "cannot conclude a restart", NOT "no restart happened".
 * The asymmetry is deliberate: the action gated on this is destroying the
 * user's in-flight turn, so the burden of proof sits on the abort.
 */
export function runtimeDefinitelyRestarted(
  before: RuntimeBootRead | null,
  after: RuntimeBootRead | null,
): boolean {
  if (!before || !after) return false;
  if (before.state !== "present" || after.state !== "present") return false;
  return before.bootId !== after.bootId;
}
